// ** React Imports
import { useState, useCallback, useContext, useRef } from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";
import { useTranslation } from "react-i18next";
import { Condition } from "components/conditional";
import loadable from "@loadable/component";
import { StyledCommentTextWrapper } from "./styles/CommentTextWrapper.styled";
import { StyledCommentContainer } from "./styles/CommentContainer.styled";

// ** Custom Components
import Block from "components/layout/Block";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import DatePresenter from "components/other/DatePresenter";
import InteractiveCommentTextEditor from "./components/InteractiveCommentTextEditor";
import LoadMoreDeepInteraciveCommentsBtn from "./components/LoadMoreDeepInteraciveCommentsBtn";
import DeepInteractiveComment from "./DeepInteractiveComment";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import pseudoFormat from "utils/pseudoFormat";
import convertText from "utils/convertText";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

import { ActionButton } from "components/layout/ActionButton";
import { openAuthModal } from "store/modules/global-common.slice";

// ** Lazy Imports
const TextEditor = loadable(
	() => import("components/layout/TextEditor/TextEditor"),
	{ ssr: false }
);

// #####################################################

const InteractiveComment = ({
	comment,
	media,
	profileUserId,
	visibleEditor,
	setVisibleEditor,
	canLike,
	commentsDisabled,
	linkOnButtonToPayments,
	cantCommentMessage,
	setUserLimit,
	deleteComment,
	sizeUserAvatar,
	closeModal,
	inModal,
	setCommentsList = null,
	setModalEditedContent,
}) => {
	const currentLoggedUser = useSelector((state) => state.globalUser);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const { isPhone, isTablet } = useContext(deviceTypeContext);
	const isUserComment = currentLoggedUser.id === comment?.user_id;
	const suspensionDuration = useSelector(
		(state) => state.globalUser?.suspension?.duration
	);
	const isBan = suspensionDuration > 0;

	const { t } = useTranslation(["common", "suspensions", "pages"]);

	const editor = useRef(null);
	const [likesCount, setLikesCount] = useState(comment?.info?.likes || 0);
	const [iLiked, setILiked] = useState(comment?.info?.i_liked || false);
	const [disableLikeButton, setDisableLikeButton] = useState(false);
	const [commentsCount, setCommentsCount] = useState(
		comment?.info?.comments || 0
	);
	const [iCommented, setICommented] = useState(
		comment?.info?.i_commented || false
	);
	const [isPendingCommentEdit, setPendingCommentEdit] = useState(false);
	const [loadingComments, setLoadingComments] = useState(false);
	const [addingDeepCommentInProgress, setAddingDeepCommentInProgress] =
		useState(false);
	const [isBeingRemoved, setIsBeingRemoved] = useState(false);
	const [deepCommentsList, setDeepCommentsList] = useState({
		data: comment?.info?.answers || [],
		total: comment?.info?.comments || 0,
	});

	const isBeingEditedComment =
		visibleEditor.type === "EDIT_COMMENT" &&
		visibleEditor.id === comment?.id;

	const dispatch = useDispatch();

	const handleLikeButtonClick = useCallback(() => {
		if (!isAuth) {
			dispatch(openAuthModal());
			return;
		}

		setDisableLikeButton(true);
		if (iLiked) {
			api.delete(
				`/like/comment?user=${comment?.user_id}&id=${media.id}&id_comment=${comment?.id}&type=${media.media_type}`,
				{ noTimeout: true }
			)
				.then(() => {
					setLikesCount((prevState) =>
						prevState > 0 ? prevState - 1 : 0
					);
					setILiked(false);

					if (typeof setCommentsList === "function") {
						setCommentsList((prev) => {
							return {
								...prev,
								data: prev.data.map((item) => {
									if (item.id !== comment.id) {
										return item;
									} else {
										return {
											...item,
											info: {
												...item.info,
												likes: likesCount - 1,
												i_liked: false,
											},
										};
									}
								}),
							};
						});
					}
				})
				.catch((error) => {
					if (error?.response?.data?.code === 99) {
						setLikesCount((prevState) =>
							prevState > 0 ? prevState - 1 : 0
						);
						setILiked(false);
					}

					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				})
				.finally(() => {
					setDisableLikeButton(false);
				});
		} else {
			api.put(
				`/like/comment?user=${comment?.user_id}&id=${media.id}&id_comment=${comment?.id}&type=${media.media_type}`,
				{},
				{ noTimeout: true }
			)
				.then(() => {
					setILiked(true);
					setLikesCount((prevState) => prevState + 1);

					if (typeof setCommentsList === "function") {
						setCommentsList((prev) => {
							return {
								...prev,
								data: prev.data.map((item) => {
									if (item.id !== comment.id) {
										return item;
									} else {
										return {
											...item,
											info: {
												...item.info,
												likes: likesCount + 1,
												i_liked: true,
											},
										};
									}
								}),
							};
						});
					}
				})
				.catch((error) => {
					if (error?.response?.data?.code === 99) {
						setILiked(true);
						setLikesCount((prevState) => prevState + 1);
					}

					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				})
				.finally(() => {
					setDisableLikeButton(false);
				});
		}

		if (typeof setModalEditedContent === "function") {
			setModalEditedContent(media.id);
		}
	}, [comment?.info?.i_liked, iLiked, comment?.user_id, media?.id]);

	const handleCommentButtonClick = () => {
		if (isAuth) {
			if (
				visibleEditor.type === "ADD_ANSWER" &&
				visibleEditor.id === comment?.id
			) {
				setVisibleEditor({
					type: null,
					id: null,
				});
			} else {
				setVisibleEditor({
					type: "ADD_ANSWER",
					id: comment?.id,
				});
			}
		} else {
			dispatch(openAuthModal());
		}
	};

	const handleEditButtonClick = () => {
		if (
			visibleEditor.type === "EDIT_COMMENT" &&
			visibleEditor.id === comment?.id
		) {
			setVisibleEditor({
				type: null,
				id: null,
			});
		} else {
			setVisibleEditor({
				type: "EDIT_COMMENT",
				id: comment?.id,
			});
		}
	};

	const handleRemoveButtonClick = () => {
		setIsBeingRemoved(true);
		api.delete(`/comment?type=${media.media_type}&id=${comment?.id}`, {
			noTimeout: true,
		})
			.then(({ data } = {}) => {
				if (typeof setUserLimit === "function") {
					setUserLimit(data?.user_limit);
				}
				setIsBeingRemoved(false);
				deleteComment(data, comment?.id);

				if (typeof setModalEditedContent === "function") {
					setModalEditedContent(media.id);
				}
			})
			.catch((error) => {
				setIsBeingRemoved(false);
				if (error?.response?.data?.error) {
					toast.error(`${error.response.data.error}`);
				}
			});
	};

	const editCommentButton = {
		onClick() {
			const { plain } = editor.current.serialize();
			if (!plain.replace(/\u200B/g, "").trim().length) {
				return;
			}
			setPendingCommentEdit(true);
			api.put(
				"/comment",
				{
					user: comment?.user_id,
					id: comment?.id,
					type: media.media_type,
					comment: plain,
				},
				{ noTimeout: true }
			)
				.then(({ data } = {}) => {
					if (!data) {
						return;
					}
					comment.comment = data?.text;
					comment.comment_edit = data?.text_edit;
					comment.comment_raw_edit = data?.text_raw_edit;
					comment.info.edited = true;
					setPendingCommentEdit(false);
					setVisibleEditor({
						type: null,
						id: null,
					});

					if (typeof setModalEditedContent === "function") {
						setModalEditedContent(media.id);
					}
				})
				.catch((error) => {
					setPendingCommentEdit(false);
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				});
		},
		disabled: isPendingCommentEdit || isBan,
		label: t("common:save"),
	};

	const fetchDeepComments = ({ limit }) => {
		setLoadingComments(true);
		api.get(
			`/comment-answer/file?user=${profileUserId}&id=${comment?.id}&id_media=${media.id}&type=${media.media_type}&limit=${limit}`,
			{ noTimeout: true }
		)
			.then(({ data } = {}) => {
				setLoadingComments(false);
				if (!data) {
					return;
				}

				const updatedComment = {
					...comment,
					info: {
						...comment.info,
						answers: data.comments,
					},
				};
				setDeepCommentsList({
					data: data.comments,
					total: data.total,
				});

				setCommentsList((prev) => ({
					...prev,
					data: prev.data.map((c) =>
						c.id === updatedComment.id ? updatedComment : c
					),
				}));
			})
			.catch((error) => {
				setLoadingComments(false);
				if (error?.response?.data?.error) {
					toast.error(`${error.response.data.error}`);
				}
			});
	};

	const addDeepComment = (text = "") => {
		setAddingDeepCommentInProgress(true);
		api.post(
			"/comment-answer",
			{
				user: profileUserId,
				id: comment?.id,
				id_media: media.id,
				type: media.media_type,
				comment: text,
			},
			{ noTimeout: true }
		)
			.then(({ data } = {}) => {
				if (typeof setUserLimit === "function") {
					setUserLimit(data?.user_limit);
				}

				if (data?.comments) {
					setICommented(true);
					setCommentsCount(data.total);
				}
				setDeepCommentsList({
					data: data?.comments || [],
					total: data?.total || 0,
				});
				setAddingDeepCommentInProgress(false);
				setVisibleEditor({
					type: null,
					id: null,
				});

				if (typeof setCommentsList === "function") {
					setCommentsList((prev) => {
						return {
							...prev,
							data: prev.data.map((item) => {
								if (item.id !== comment.id) {
									return item;
								} else {
									const newItem = item;
									newItem.info.answers = data?.comments || [];
									newItem.info.comments = data?.total || 0;
									newItem.info.i_commented =
										data?.main_i_commented || false;

									return newItem;
								}
							}),
						};
					});
				}

				if (typeof setModalEditedContent === "function") {
					setModalEditedContent(media.id);
				}
			})
			.catch((error) => {
				setAddingDeepCommentInProgress(false);
				if (error?.response?.data?.error) {
					toast.error(`${error.response.data.error}`);
				}
			});
	};

	const deleteDeepComment = (data) => {
		if (typeof setUserLimit === "function") {
			setUserLimit(data?.user_limit);
		}
		setCommentsCount(data?.total || 0);
		setICommented(data?.main_i_commented || false);

		if (typeof setCommentsList === "function") {
			setCommentsList((prev) => {
				return {
					...prev,
					data: prev.data.map((item) => {
						if (item.id !== comment.id) {
							return item;
						} else {
							const newItem = item;
							newItem.info.answers = data?.comments || [];
							newItem.info.comments = data?.total || 0;
							newItem.info.i_commented =
								data?.main_i_commented || false;

							return newItem;
						}
					}),
				};
			});
		}
		if (typeof setModalEditedContent === "function") {
			setModalEditedContent(media.id);
		}
	};

	const tooltipLikeText = () => {
		if (isBan) {
			return t("suspensions:bannedAccount");
		} else if (!canLike) {
			return t("common:canNotLikePost");
		} else return "";
	};

	const labelText = (counters) => {
		if (isPhone) {
			return counters > 0 ? pseudoFormat(counters) : "";
		} else {
			return counters > 0 ? ` ${pseudoFormat(counters)}` : "";
		}
	};
	return (
		<StyledCommentContainer>
			<UserMediaSkeletor
				addBgColor
				changeSizeOfContent={isBeingEditedComment}
				noMarginBottom
				user={comment}
				size={sizeUserAvatar}
				closeModal={closeModal}
				headerLeft={
					<Block>
						<Block small color="grey">
							<DatePresenter date={comment?.date_add} />
							{comment?.info.edited && ` (${t("common:edited")})`}
						</Block>
						<Condition flex column value={!isBeingEditedComment}>
							<StyledCommentTextWrapper>
								{convertText({
									pieces: comment?.comment,
								})}
							</StyledCommentTextWrapper>
						</Condition>
						<Condition value={isBeingEditedComment}>
							<Block mt={2}>
								<TextEditor
									ref={editor}
									buttonCancel={{
										onClick: () => {
											handleEditButtonClick();
										},
									}}
									button={editCommentButton}
									initValue={{
										plain: comment?.comment_raw_edit,
										parsed: comment?.comment_edit,
									}}
									disabled={isPendingCommentEdit || isBan}
									singleLine
									scrollOnFocus
									tooltipInfo={
										isBan
											? t("suspensions:bannedAccount")
											: ""
									}
									inModal={inModal}
									placeholder={t("common:placeholder.write")}
								/>
							</Block>
						</Condition>
					</Block>
				}
				content={
					<Block flex column>
						<Condition flex column value={!isBeingEditedComment}>
							<Block flex full alignCenter justifyBetween mt={1}>
								<Block flex style={{ maxWidth: "99%" }}>
									<ActionButton
										isHeart
										onClick={handleLikeButtonClick}
										disabled={
											!canLike ||
											disableLikeButton ||
											isBan
										}
										noColorDisabled={disableLikeButton}
										liked={iLiked}
										label={
											likesCount > 0
												? pseudoFormat(likesCount)
												: null
										}
										labelSize="0.8rem"
										customSvgSize="15px"
										customMargin="0 0 0 0"
										tooltipInfo={tooltipLikeText()}
									/>
									<ActionButton
										isComment
										isActive={
											visibleEditor.type ===
												"ADD_ANSWER" &&
											visibleEditor.id === comment?.id
										}
										noHover={isPhone || isTablet}
										disabled={addingDeepCommentInProgress}
										tooltipInfo={cantCommentMessage}
										onClick={handleCommentButtonClick}
										commented={iCommented}
										label={labelText(commentsCount)}
										labelSize="0.8rem"
										customSvgSize="15px"
									/>
									<Condition value={isUserComment}>
										<ActionButton
											isPen
											onClick={handleEditButtonClick}
											disabled={isBan || isBeingRemoved}
											tooltipInfo={
												isBan
													? t(
															"suspensions:bannedAccount"
													  )
													: ""
											}
											customSvgSize={"13px"}
										/>
										<ActionButton
											isTrash
											onClick={handleRemoveButtonClick}
											disabled={isBeingRemoved}
											customSvgSize={"13px"}
											tooltipInfo={""}
										/>
									</Condition>
								</Block>
							</Block>
						</Condition>

						<InteractiveCommentTextEditor
							editor={editor}
							isEditorVisible={
								visibleEditor.type === "ADD_ANSWER" &&
								visibleEditor.id === comment?.id
							}
							accept={addDeepComment}
							isAuth={isAuth}
							currentLoggedUser={currentLoggedUser}
							disableEditor={
								commentsDisabled ||
								addingDeepCommentInProgress ||
								linkOnButtonToPayments
							}
							cantCommentMessage={cantCommentMessage}
							linkOnButtonToPayments={linkOnButtonToPayments}
							inModal={inModal}
						/>
						<Block mt={deepCommentsList?.data?.length ? 3 : 0}>
							{deepCommentsList?.data?.map((deepComment) => (
								<DeepInteractiveComment
									key={deepComment?.id}
									deepComment={deepComment}
									comment={comment}
									media={media}
									currentLoggedUser={currentLoggedUser}
									visibleEditor={visibleEditor}
									setVisibleEditor={setVisibleEditor}
									deepCommentsList={deepCommentsList}
									isPhone={isPhone}
									isAuth={isAuth}
									tooltipLikeText={tooltipLikeText}
									canLike={canLike}
									isBan={isBan}
									deleteDeepComment={deleteDeepComment}
									sizeUserAvatar="xsmall"
									setDeepCommenstList={setDeepCommentsList}
									inModal={inModal}
									setCommentsList={setCommentsList}
									setModalEditedContent={
										setModalEditedContent
									}
								/>
							))}
							<LoadMoreDeepInteraciveCommentsBtn
								isPhone={isPhone}
								isAuth={isAuth}
								deepCommentsList={deepCommentsList}
								fetchDeepComments={fetchDeepComments}
								loadingComments={loadingComments}
							/>
						</Block>
					</Block>
				}
			/>
		</StyledCommentContainer>
	);
};

export default InteractiveComment;
